import React, { useState } from 'react';
import {
  Badge,
  Box,
  ButtonSelect,
  Icon,
  InputField,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../utils/localization';

import { type ActiveFilters } from './helpers/filterUtils';
import FilterPills, { type FilterOption, type FilterPill } from './FilterPills';
import invertedMessages from './messages';

import './EntitlementsToolbar.css';

interface EntitlementsToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onFilterClick: () => void;
  onRowsClick: () => void;
  onColumnsClick: () => void;
  onFilterChange: (filterId: string, selectedOptions: FilterOption[]) => void;
  onClearAllFilters?: () => void;
  hasActiveFilters?: boolean;
  hasExpandedRows?: boolean;
  // Filter state props - following controlled component pattern
  filterPills?: FilterPill[];
  activeFilters?: ActiveFilters;
  isFilterLoading?: boolean;
  filterError?: string | null;
  // Future props for dropdown states, filter counts, etc.
}

const defaultProps: Partial<EntitlementsToolbarProps> = {
  filterPills: [],
  activeFilters: { applications: [], entitlements: [], access_status: [] },
  isFilterLoading: false,
  filterError: null,
  onClearAllFilters: undefined,
  hasActiveFilters: false,
  hasExpandedRows: false,
};

function EntitlementsToolbar({
  searchValue,
  onSearchChange,
  onFilterClick,
  onRowsClick,
  onColumnsClick,
  onFilterChange,
  onClearAllFilters,
  hasActiveFilters = false,
  hasExpandedRows = false,
  filterPills = [],
  activeFilters = { applications: [], entitlements: [], access_status: [] },
  isFilterLoading = false,
  filterError = null,
}: EntitlementsToolbarProps): JSX.Element {
  const l = useInvertedLocalize(invertedMessages);

  // local state for button open/close states (for visual feedback)
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isRowsOpen, setIsRowsOpen] = useState(false);
  const [isColumnsOpen, setIsColumnsOpen] = useState(false);

  // State for filter pills visibility
  const [isFilterPillsVisible, setIsFilterPillsVisible] = useState(false);

  const handleFilterClick = () => {
    setIsFilterOpen(!isFilterOpen);
    setIsFilterPillsVisible(!isFilterPillsVisible);
    onFilterClick();
  };

  const handleRowsClick = () => {
    setIsRowsOpen(!isRowsOpen);
    onRowsClick();
  };

  const handleColumnsClick = () => {
    setIsColumnsOpen(!isColumnsOpen);
    onColumnsClick();
  };

  return (
    <Box tag='section' className='EntitlementsToolbar'>
      {/* Main toolbar row with search and controls */}
      <Box className='EntitlementsToolbar-main'>
        <Box className='EntitlementsToolbar-searchContainer'>
          <InputField
            name='entitlementsSearch'
            placeholder={l('Search Entitlements')}
            kind='search'
            size='medium'
            value={searchValue}
            setValue={onSearchChange}
            prefixIcon={
              <Icon kind='Search' color='--color-foreground-neutral-subtle' />
            }
            className='EntitlementsToolbar-searchField'
          />
        </Box>

        <Box className='EntitlementsToolbar-controls'>
          <ButtonSelect
            kind='rectangle'
            label={l('Filter')}
            size='medium'
            onClick={handleFilterClick}
            isOpen={isFilterOpen}
            prefixIcon={<Icon kind='Filter' />}
            className='EntitlementsToolbar-filterButton'
            BadgeComp={
              hasActiveFilters ? (
                <Badge kind='dot' color='primary' size='small' />
              ) : null
            }
          />
          <ButtonSelect
            kind='rectangle'
            label={l('Collapse All')}
            size='medium'
            isVisualCueEnabled={false}
            onClick={handleRowsClick}
            isDisabled={!hasExpandedRows}
          />
          {/* 
          TODO: MVP2 - look into integrating this button
          in place of 'collapse all'. */}
          <ButtonSelect
            kind='rectangle'
            label={l('Rows')}
            size='medium'
            onClick={handleRowsClick}
            isOpen={isRowsOpen}
            className='EntitlementsToolbar-rowsButton'
          />

          <ButtonSelect
            kind='rectangle'
            label={l('Columns')}
            size='medium'
            onClick={handleColumnsClick}
            isOpen={isColumnsOpen}
            className='EntitlementsToolbar-columnsButton'
          />
        </Box>
      </Box>

      {/* Filter pills row - appears below main toolbar */}
      <FilterPills
        isVisible={isFilterPillsVisible}
        filterPills={filterPills}
        activeFilters={activeFilters}
        isLoading={isFilterLoading}
        error={filterError}
        onFilterChange={onFilterChange}
        onClearAll={onClearAllFilters}
      />
    </Box>
  );
}

EntitlementsToolbar.defaultProps = defaultProps;

export default EntitlementsToolbar;
